import {
  Avatar,
  Box,
  IconButton,
  LinkBox,
  LinkOverlay,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as queryString from 'query-string';
import * as React from 'react';
import { Hit } from 'react-instantsearch-core';

import { IMemberSearch } from '@/client/legacy-shared-modules/search/search-types';
import { EAttributeOwnerKind } from '@/graphql-types/globalTypes';
import { useCurrent } from '@/hooks/useCurrent';
import { useHighlightedAttributes } from '@/hooks/useHighlightedAttributes';
import { useMemberManagement } from '@/hooks/useMemberManagement';
import { usePermissions } from '@/hooks/usePermissions';
import {
  MailIcon,
  MapPinIcon,
  MessageCircleIcon,
  MoreHorizontalIcon,
  PhoneIcon,
} from '@/imports/ui/chakra/feather';
import { Highlight } from '@/imports/ui/components/algolia/Highlight';
import { useMemberDirectory } from '@/imports/ui/hooks/useMemberDirectory';
import { tokenStorage } from '@/modules/accounts/tokenStorage';
import { showChatIconInMobile } from '@/utilities';

const ATTRIBUTES_WITH_CUSTOM_DISPLAY_RULES = [
  'firstName',
  'lastName',
  'displayName',
  'nameTitle',
  'jobTitle',
  'company',
  'email',
  'primaryPhone',
];

export const Member: React.FC<{
  member: Hit<IMemberSearch>;
  facetFilters?: string[];
}> = (props) => {
  const { facetFilters, member } = props;
  const permissions = usePermissions();
  const current = useCurrent();
  const { isActive, loading } = useMemberManagement();
  const {
    loading: useMemberDirectoryLoading,
    viewableAttributesField,
    searchableAttributesField,
  } = useMemberDirectory();

  const currentTimeMs = React.useRef(new Date().getTime()).current;

  const showChatIcon = showChatIconInMobile();

  const isMember = React.useMemo(() => {
    if (loading) {
      return false;
    }

    if (isActive) {
      const startedBeforeNow =
        member.membershipData?.startDateMs &&
        member.membershipData.startDateMs !== -1 &&
        member.membershipData.startDateMs < currentTimeMs;
      const gracePeriodAfterNow =
        member.membershipData?.gracePeriodEndDateMs &&
        member.membershipData.gracePeriodEndDateMs !== -1 &&
        member.membershipData.gracePeriodEndDateMs > currentTimeMs;
      const endDateAfterNow =
        member.membershipData?.endDateMs &&
        member.membershipData.endDateMs !== -1 &&
        member.membershipData.endDateMs > currentTimeMs;
      const endDateDoesntExist =
        member.membershipData?.endDateMs &&
        member.membershipData.endDateMs === -1;
      return (
        startedBeforeNow &&
        (gracePeriodAfterNow || endDateAfterNow || endDateDoesntExist)
      );
    } else {
      return member.integrationIsMember;
    }
  }, [
    currentTimeMs,
    isActive,
    loading,
    member.integrationIsMember,
    member.membershipData,
  ]);

  const typedViewableAttributesField = viewableAttributesField as
    | 'adminMemberDirectoryViewable'
    | 'membersWithMemberStatusMemberDirectoryViewable'
    | 'loggedInMemberDirectoryViewable';
  const memberAttributes = member[typedViewableAttributesField];

  const attributesToHighlight = useHighlightedAttributes(
    EAttributeOwnerKind.USER,
    member._highlightResult,
    searchableAttributesField,
    facetFilters
  ).filter(
    (attribute) => !ATTRIBUTES_WITH_CUSTOM_DISPLAY_RULES.includes(attribute._id)
  );

  if (
    loading ||
    current.loading ||
    useMemberDirectoryLoading ||
    !viewableAttributesField ||
    !searchableAttributesField ||
    !memberAttributes
  ) {
    return null;
  }

  return (
    <LinkBox>
      <Stack direction="row" align="flex-start" spacing="4">
        <Avatar
          src={member.profilePictureUrl || undefined}
          name={member.displayName}
          boxSize={10}
        />
        <Stack
          direction={{ base: 'column', lg: 'row' }}
          flex="1"
          spacing="4"
          align={{ lg: 'center' }}
        >
          <Box flex="1">
            <LinkOverlay as={NextLink} href={`/members/${member.userId}`}>
              <Text
                color="gray.700"
                noOfLines={1}
                wordBreak="break-all"
                textStyle="bodyStrong"
              >
                <Highlight attribute="displayName" hit={member} />
                {memberAttributes.nameTitle ? (
                  <>
                    ,{' '}
                    <Highlight
                      attribute="nameTitle"
                      hit={member}
                      searchableAttributesField={searchableAttributesField}
                      viewableAttributesField={viewableAttributesField}
                    />
                  </>
                ) : null}
              </Text>
            </LinkOverlay>
            <Text color="gray.600" mt="2">
              <Highlight
                attribute="jobTitle"
                hit={member}
                searchableAttributesField={searchableAttributesField}
                viewableAttributesField={viewableAttributesField}
              />
              {memberAttributes.company && memberAttributes.jobTitle
                ? ' at '
                : ''}
              <Highlight
                attribute="company"
                hit={member}
                searchableAttributesField={searchableAttributesField}
                viewableAttributesField={viewableAttributesField}
              />
            </Text>
            <Wrap color="gray.600" mt="2">
              {memberAttributes.email && (
                <WrapItem>
                  <Text wordBreak="break-word">
                    <MailIcon mr="1" boxSize={3} color="gray.700" />
                    <Highlight
                      attribute="email"
                      hit={member}
                      searchableAttributesField={searchableAttributesField}
                      viewableAttributesField={viewableAttributesField}
                    />
                  </Text>
                </WrapItem>
              )}
              {memberAttributes.primaryPhone && (
                <WrapItem>
                  <Text>
                    <PhoneIcon mr="1" boxSize={3} color="gray.700" />
                    <Highlight
                      attribute="primaryPhone"
                      hit={member}
                      searchableAttributesField={searchableAttributesField}
                      viewableAttributesField={viewableAttributesField}
                    />
                  </Text>
                </WrapItem>
              )}
              {memberAttributes.addressLocality ||
              memberAttributes.addressAdministrativeArea ||
              memberAttributes.addressRegionCode ? (
                <WrapItem>
                  <Text>
                    <MapPinIcon mr="1" boxSize={3} color="gray.700" />
                    {memberAttributes.addressLocality && (
                      <Highlight
                        attribute={'addressLocality'}
                        hit={member}
                        searchableAttributesField={searchableAttributesField}
                        viewableAttributesField={viewableAttributesField}
                      />
                    )}
                    {(memberAttributes.addressAdministrativeArea ||
                      memberAttributes.addressRegionCode) && (
                      <>
                        {memberAttributes.addressLocality && ', '}
                        {memberAttributes.addressAdministrativeArea && (
                          <Highlight
                            attribute={'addressAdministrativeArea'}
                            hit={member}
                            searchableAttributesField={
                              searchableAttributesField
                            }
                            viewableAttributesField={viewableAttributesField}
                          />
                        )}
                        {memberAttributes.addressAdministrativeArea &&
                          memberAttributes.addressRegionCode &&
                          ', '}
                        {memberAttributes.addressRegionCode && (
                          <Highlight
                            attribute={'addressRegionCode'}
                            hit={member}
                            searchableAttributesField={
                              searchableAttributesField
                            }
                            viewableAttributesField={viewableAttributesField}
                          />
                        )}
                      </>
                    )}
                  </Text>
                </WrapItem>
              ) : null}
              {attributesToHighlight.map((attribute) => {
                return (
                  <WrapItem key={attribute._id}>
                    <Text color="gray.600">
                      {attribute.name}:{' '}
                      <Highlight
                        attribute={attribute._id}
                        hit={member}
                        searchableAttributesField={searchableAttributesField}
                        viewableAttributesField={viewableAttributesField}
                      />
                    </Text>
                  </WrapItem>
                );
              })}
            </Wrap>
          </Box>
          <Stack direction="row">
            {current.user?._id !== member.userId &&
              permissions.canAccessCommunityAndAuthenticated &&
              !tokenStorage.getItem('isMobile') && (
                <NextLink
                  href={`/messages/create?${queryString.stringify({
                    channel_member: member.userId,
                  })}`}
                >
                  <IconButton
                    aria-label="Direct Message"
                    icon={<MessageCircleIcon />}
                    variant="outline"
                    size="md"
                    colorScheme="gray"
                  />
                </NextLink>
              )}
            {permissions.canDoAdminStuffAndAuthenticated && (
              <Box>
                <Menu autoSelect={false}>
                  <MenuButton
                    as={IconButton}
                    aria-label="See more"
                    icon={<MoreHorizontalIcon />}
                    variant="outline"
                    size="md"
                    colorScheme="gray"
                  />
                  <MenuList>
                    <NextLink
                      href={`/admin/membership/people/${member.userId}`}
                    >
                      <MenuItem>Edit Profile</MenuItem>
                    </NextLink>
                  </MenuList>
                </Menu>
              </Box>
            )}
          </Stack>
        </Stack>
      </Stack>
    </LinkBox>
  );
};
